import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type FormikProps } from 'formik';
import Loading from '@components/Shared/Loader/Loading';
import { Form } from 'react-bootstrap';
import NeatixModal from '@components/Modal/NeatixModal';
import CodeCombination from './CodeCombination';
import PopoverCustom from '@components/Shared/Popover/PopoverCustom';
import SwalAlert from '@components/Shared/Alert/SwalAlert';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import ToggleButton from 'react-bootstrap/ToggleButton';
import useGetTaxRateByOrganization from '@hooks/Finance/TaxRate/useGetTaxRateByOrganization';
import { useAppSelector } from '@store/hooks';
import { activeFinanceSelector } from '@store/Login/authSelectors';
import { Card } from '@components/card/card';
import { NumericFormat } from 'react-number-format';
import useGlChartLookups from '@hooks/Finance/ChartAccountsDetails/useGlChartLookups';

interface LinesProps {
  formik: FormikProps<any>;
  NotEditable?: boolean;
  maxTableHeight?: string;
}

const ApInvoiceLines: React.FC<LinesProps> = ({ formik, NotEditable = false, maxTableHeight = '400px' }) => {
  const { values } = formik;
  const { i18n, t } = useTranslation();
  const activeFinance = useAppSelector(activeFinanceSelector);

  const { OptionsCostCenters, OptionsCompanies, OptionsBranches, OptionsAccounts, GetCodeCombination, loading, error } =
    useGlChartLookups();

  const [shawModalAcaount, setShawModalAcaount] = useState(false);
  const [selectedLineIndex, setSelectedLineIndex] = useState<number | null>(null);

  const { taxRate } = useGetTaxRateByOrganization({
    dateActive: values.invoiceDate,
    taxType: 'Purchase',
    organizationId: values.organizationId
  });

  const formatNumber = (value: number) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value || 0));

  const round2 = (n: number) => Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;
  const handleLineChange = (index: number, field: string, value: any) => {
    const updatedLines = [...values.apInvoiceLinesList];
    const line = updatedLines[index];

    updatedLines[index] = {
      ...line,
      [field]: value
    };

    // حساب القيم المالية
    const qty = Number(updatedLines[index].qty);
    const price = Number(updatedLines[index].price);
    const vatRate = Number(updatedLines[index].vatRate);
    const includeVatFlag = values.includeVatFlag;
    let discountAmount = Number(updatedLines[index].discountAmount);
    let discountRate = Number(updatedLines[index].discountRate);

    // حساب الخصم بناءً على قيمة الخصم أو نسبة الخصم
    if (field === 'discountAmount') {
      discountRate = price * qty > 0 ? (discountAmount / (price * qty)) * 100 : 0;
    } else if (field === 'discountRate') {
      discountAmount = (price * qty * discountRate) / 100;
    }

    const subtotal = round2(price * qty - discountAmount);
    const vatAmount = includeVatFlag ? round2((subtotal * vatRate) / 100) : 0;
    const total = round2(subtotal + vatAmount);

    // تحديث السطر بالقيم المحسوبة
    updatedLines[index] = {
      ...updatedLines[index],
      discountAmount,
      discountRate,
      vatAmount,
      total
    };

    // تحديث القيم في النموذج
    formik?.setFieldValue('apInvoiceLinesList', updatedLines);
  };

  const handleAddLine = () => {
    const newLine = {
      id: null,
      lineType: 'Item',
      lineNumber: values.apInvoiceLinesList?.length + 1,
      invItemId: null,
      uom: '',
      price: 0,
      qty: 1,
      discountRate: 0,
      discountAmount: 0,
      vatRate: taxRate || 15,
      total: 0,
      vatAmount: 0,
      description: '',
      currencyCode: '',
      currencyConversionRate: 1,
      purHeaderId: null,
      purLineId: null,
      accountId: null,
      costCenterId: null,
      companyId: '',
      branchId: '',
      codeCombination: ''
    };
    formik.setFieldValue('apInvoiceLinesList', [...(formik.values.apInvoiceLinesList || []), newLine]);
  };

  const handleDeleteLine = (index: number) => {
    const updatedLines = values.apInvoiceLinesList?.filter((_: any, i: number) => i !== index);
    formik?.setFieldValue('apInvoiceLinesList', updatedLines);
  };

  // ✅ Subtotal (قبل الضريبة)
  // في حالة شامل الضريبة: line.total يحتوي VAT، فنطرح vatAmount للحصول على الصافي قبل الضريبة
  const calculateTotalSum = () => {
    const subtotal = (values.apInvoiceLinesList || []).reduce((sum: number, line: any) => {
      const lineTotal = Number(line.total || 0);
      const lineVat = Number(line.vatAmount || 0);
      return sum + (values.includeVatFlag ? lineTotal - lineVat : lineTotal);
    }, 0);
    return formatNumber(subtotal);
  };

  const calculateVatSum = () => {
    const vatTotal = (values.apInvoiceLinesList || []).reduce(
      (sum: number, line: any) => sum + Number(line.vatAmount || 0),
      0
    );
    return formatNumber(vatTotal);
  };

  // ✅ Grand Total: مجموع line.total (وهو بالفعل شامل VAT عندما includeVatFlag=true)
  const calculateNetTotal = () => {
    const grandTotal = (values.apInvoiceLinesList || []).reduce(
      (sum: number, line: any) => sum + Number(line.total || 0),
      0
    );
    return formatNumber(grandTotal);
  };

  const [accountDetails, setAccountDetails] = useState({
    companyId: '',
    branchId: '',
    costCenterId: '',
    accountId: ''
  });

  const handleAccountClick = (index: number) => {
    if (!values.organizationId) {
      SwalAlert({ text: 'حدد الفرع' });
      return;
    }

    const selectedLine = formik.values.apInvoiceLinesList[index];
    const companyId = selectedLine.companyId || '';
    const branchId = selectedLine.branchId || '';
    const accountId = selectedLine.accountId || '';
    const costCenterId = selectedLine.costCenterId || '';

    setSelectedLineIndex(index);
    setShawModalAcaount(true);
    setAccountDetails({ accountId, costCenterId, companyId, branchId });
  };

  const handleAccountSelect = (account: any) => {
    // ✅ Cancel: فقط اغلاق المودال بدون تعديل السطر
    if (!account) {
      setShawModalAcaount(false);
      return;
    }

    if (selectedLineIndex !== null) {
      const updatedLines = [...values.apInvoiceLinesList];

      const GlCompanyId = (updatedLines[selectedLineIndex].companyId = account?.company?.id || '');
      const GlBrancheId = (updatedLines[selectedLineIndex].branchId = account?.branch?.id || '');
      const AccountId = (updatedLines[selectedLineIndex].accountId = account?.account?.id || '');
      const costCenterId = (updatedLines[selectedLineIndex].costCenterId = account?.costCenter?.id || '');

      const codeCombination = GetCodeCombination(GlCompanyId, GlBrancheId, AccountId, costCenterId);

      updatedLines[selectedLineIndex].codeCombination = codeCombination || '';

      formik.setFieldValue('apInvoiceLinesList', updatedLines);
    }
    setShawModalAcaount(false);
  };

  const handleShowAccountDetails = (index: number) => {
    const selectedLine = values.apInvoiceLinesList[index];
    const { companyId, branchId, accountId, costCenterId } = selectedLine;

    return (
      <div className='text-start'>
        <p className='m-0 mb-1 bg-theme bg-opacity-20 p-1 rounded-2'>
          {OptionsCompanies?.find(f => f.value === companyId)?.accountShow}
        </p>
        <p className='m-0 mb-1 bg-theme bg-opacity-20 p-1 rounded-2'>
          {OptionsBranches?.find(f => f.value === branchId)?.accountShow}
        </p>
        <p className='m-0 mb-1 bg-theme bg-opacity-20 p-1 rounded-2'>
          {OptionsAccounts?.find(f => f.value === accountId)?.accountShow}
        </p>
        <p className='m-0 mb-1 bg-theme bg-opacity-20 p-1 rounded-2'>
          {OptionsCostCenters?.find(f => f.value === costCenterId)?.accountShow}
        </p>
      </div>
    );
  };

  const radios = [
    { name: 'شامل الضريبة', value: true },
    { name: 'غير شامل الضريبة', value: false }
  ];

  const totals = useMemo(() => {
    const lines = values.apInvoiceLinesList || [];

    let subtotal = 0;
    let vat = 0;
    let total = 0;

    for (const l of lines) {
      const lineTotal = Number(l.total || 0);
      const lineVat = Number(l.vatAmount || 0);
      const type = (l.lineType || 'Item') as string;

      if (values.includeVatFlag) {
        subtotal += lineTotal - lineVat;
        vat += lineVat;
        total += lineTotal;
      } else {
        // ✅ لو عندك Tax line يدوي
        if (type === 'Tax') vat += lineTotal;
        else subtotal += lineTotal;

        total += lineTotal;
      }
    }

    return {
      subtotal: formatNumber(subtotal),
      vat: formatNumber(vat),
      total: formatNumber(total),
      hasVat: vat > 0
    };
  }, [values.apInvoiceLinesList, values.includeVatFlag]);

  return (
    <Loading loading={false} error={null} Type='Dots'>
      <Card className='mb-4 border-0'>
        {/* Header Section */}
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h6 className='mb-0'>
            <i className='fas fa-file-invoice-dollar me-2'></i>
            {t('بنود الفاتورة')}
          </h6>
          <div>
            <span className='badge bg-theme me-2'>عدد البنود: {(values.apInvoiceLinesList || []).length}</span>
            <button
              type='button'
              className='btn btn-theme btn-sm'
              onClick={handleAddLine}
              disabled={values.postedFlag || NotEditable}
            >
              <i className='fas fa-plus me-2'></i>
              {t('AddNewLine')}
            </button>
          </div>
        </div>

        {/* Tax Options Section */}
        <div className='card-body p-3 border-bottom'>
          <div className='d-flex flex-column flex-md-row justify-content-between align-items-center'>
            <div className='mb-3 mb-md-0'>
              <span className='fw-bold me-3'>{t('نوع الفاتورة')}:</span>
              <ButtonGroup>
                {radios.map((radio, idx) => (
                  <ToggleButton
                    key={idx}
                    id={`radio-${idx}`}
                    type='radio'
                    variant={formik.values.includeVatFlag === radio.value ? 'theme' : 'outline-theme'}
                    name='includeVatFlag'
                    value={radio.value as any}
                    checked={formik.values.includeVatFlag === radio.value}
                    onChange={() => {
                      if ((values.apInvoiceLinesList || []).length > 0 || NotEditable) return;
                      formik.setFieldValue('includeVatFlag', radio.value);
                    }}
                    disabled={(values.apInvoiceLinesList || []).length > 0 || NotEditable}
                    className='px-4'
                  >
                    {radio.name}
                  </ToggleButton>
                ))}
              </ButtonGroup>
            </div>

            <div className='text-md-end'>
              {(values.apInvoiceLinesList || []).length === 0 && (
                <small className='text-muted'>
                  <i className='fas fa-info-circle me-1'></i>
                  {t('أضف بنودًا لرؤية الإجماليات')}
                </small>
              )}
            </div>
          </div>
        </div>

        {/* Summary Section */}
        {(values.apInvoiceLinesList || []).length > 0 && (
          <div className='card-body p-3'>
            <div className='bg-inverse bg-opacity-10 p-3 rounded-0'>
              <div className='d-flex flex-wrap gap-4 justify-content-between'>
                <div className='d-flex gap-2 align-items-center'>
                  <span className='fw-bold'>{t('الإجمالي')}:</span>
                  <span className='fw-bold'>{totals.subtotal}</span>
                </div>
                {/* {values.includeVatFlag && ( */}
                {(values.includeVatFlag || totals.hasVat) && (
                  <div className='d-flex gap-2 align-items-center'>
                    <span className='fw-bold'>{t('VAT')}:</span>
                    <span className='fw-bold text-warning'>{totals.vat}</span>
                  </div>
                )}
                <div className='d-flex gap-2 align-items-center'>
                  <span className='fw-bold'>{t('الصافي')}:</span>
                  <span className='text-theme fs-5 fw-bold'>{totals.total}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Table Section */}
        <div className='card-body p-0'>
          {(values.apInvoiceLinesList || []).length > 0 ? (
            <div className='table-responsive'>
              <div
                style={{
                  maxHeight: maxTableHeight,
                  overflow: 'auto',
                  position: 'relative'
                }}
              >
                <table className='table table-hover mb-0' style={{ minWidth: '1400px' }}>
                  <thead
                    className='table-actived fw-bold'
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 100,
                      backdropFilter: 'blur(30px)',
                      backgroundColor: 'var(--bs-table-bg)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    <tr>
                      <th
                        className='text-center small'
                        style={{
                          width: '50px',
                          minWidth: '50px',
                          position: 'sticky',
                          left: 0,
                          backgroundColor: 'var(--bs-table-bg)',
                          zIndex: 101,
                          boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                        }}
                      >
                        #
                      </th>
                      <th className='text-center small' style={{ width: '100px', minWidth: '100px' }}>
                        {t('Type')}
                      </th>
                      <th className='text-center small' style={{ width: '350px', minWidth: '350px' }}>
                        {t('Description')}
                      </th>
                      <th className='text-center small' style={{ width: '70px', minWidth: '70px' }}>
                        {t('Quantity')}
                      </th>
                      <th className='text-center small' style={{ width: '90px', minWidth: '90px' }}>
                        {t('Price')}
                      </th>
                      <th className='text-center small' style={{ width: '70px', minWidth: '70px' }}>
                        {t('DiscountRate')}
                      </th>
                      <th className='text-center small' style={{ width: '90px', minWidth: '90px' }}>
                        {t('DiscountAmount')}
                      </th>
                      {values.includeVatFlag && (
                        <>
                          <th className='text-center small' style={{ width: '70px', minWidth: '70px' }}>
                            {t('VAT %')}
                          </th>
                          <th className='text-center small' style={{ width: '90px', minWidth: '90px' }}>
                            {t('VAT')}
                          </th>
                        </>
                      )}
                      <th className='text-center small' style={{ width: '100px', minWidth: '100px' }}>
                        {/* {t('Amount')} */}
                        {t('الإجمالي')}
                      </th>
                      {activeFinance && (
                        <th className='text-center small' style={{ width: '250px', minWidth: '250px' }}>
                          {t('Account')}
                        </th>
                      )}
                      <th className='text-center small' style={{ width: '50px', minWidth: '50px' }}></th>
                    </tr>
                  </thead>

                  <tbody>
                    {(values.apInvoiceLinesList || []).map((line: any, index: number) => (
                      <tr key={index}>
                        <td
                          className='text-center align-middle'
                          style={{
                            position: 'sticky',
                            left: 0,
                            backgroundColor: 'var(--bs-body-bg)',
                            zIndex: 50,
                            boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                          }}
                        >
                          <span className='badge bg-theme bg-opacity-30 fw-bold' style={{ fontSize: '0.85rem' }}>
                            {index + 1}
                          </span>
                        </td>

                        <td className='align-middle'>
                          <Form.Select
                            disabled={NotEditable}
                            className='form-select form-select-sm border-theme'
                            name='lineType'
                            value={line.lineType || ''}
                            onChange={e => handleLineChange(index, 'lineType', e.target.value)}
                            style={{ minWidth: '90px' }}
                          >
                            <option value='Item'>{i18n.language === 'en' ? 'Item' : 'صنف'}</option>
                            <option value='Tax' hidden={values.includeVatFlag}>
                              {i18n.language === 'en' ? 'Tax' : 'ضريبة'}
                            </option>
                            <option value='Expenses'>{i18n.language === 'en' ? 'Expenses' : 'مصروفات'}</option>
                          </Form.Select>
                        </td>

                        {/* الوصف - طويل جداً */}
                        <td className='align-middle'>
                          <Form.Control
                            disabled={NotEditable}
                            className='form-control form-control-sm border-theme'
                            as='textarea'
                            rows={1}
                            value={line.description || ''}
                            onChange={e => handleLineChange(index, 'description', e.target.value)}
                            style={{
                              minHeight: '38px',
                              minWidth: '340px',
                              resize: 'vertical'
                            }}
                          />
                        </td>

                        {/* الأعمدة الرقمية - صغيرة */}
                        <td className='align-middle'>
                          <NumericFormat
                            disabled={NotEditable}
                            name={`line-${index}-qty`}
                            value={line.qty}
                            thousandSeparator=','
                            decimalSeparator='.'
                            decimalScale={2}
                            fixedDecimalScale={false}
                            allowNegative={false}
                            className='form-control form-control-sm border-theme text-center'
                            onValueChange={v => handleLineChange(index, 'qty', v.floatValue ?? 0)}
                            style={{ width: '100%', minWidth: '60px' }}
                          />
                        </td>

                        <td className='align-middle'>
                          <NumericFormat
                            disabled={NotEditable}
                            name={`line-${index}-price`}
                            value={line.price}
                            thousandSeparator=','
                            decimalSeparator='.'
                            decimalScale={2}
                            fixedDecimalScale={false}
                            allowNegative={false}
                            className='form-control form-control-sm border-theme text-center'
                            onValueChange={v => handleLineChange(index, 'price', v.floatValue ?? 0)}
                            style={{ width: '100%', minWidth: '80px' }}
                          />
                        </td>

                        <td className='align-middle'>
                          <NumericFormat
                            disabled={NotEditable}
                            name={`line-${index}-discountRate`}
                            value={line.discountRate}
                            thousandSeparator=','
                            decimalSeparator='.'
                            decimalScale={2}
                            fixedDecimalScale={false}
                            allowNegative={false}
                            isAllowed={v => v.floatValue === undefined || v.floatValue <= 100}
                            className='form-control form-control-sm border-theme text-center'
                            onValueChange={v => handleLineChange(index, 'discountRate', v.floatValue ?? 0)}
                            style={{ width: '100%', minWidth: '60px' }}
                          />
                        </td>

                        <td className='align-middle'>
                          <NumericFormat
                            disabled={NotEditable}
                            name={`line-${index}-discountAmount`}
                            value={line.discountAmount}
                            thousandSeparator=','
                            decimalSeparator='.'
                            decimalScale={2}
                            fixedDecimalScale={false}
                            allowNegative={false}
                            className='form-control form-control-sm border-theme text-center'
                            onValueChange={v => handleLineChange(index, 'discountAmount', v.floatValue ?? 0)}
                            style={{ width: '100%', minWidth: '80px' }}
                          />
                        </td>

                        {values.includeVatFlag && (
                          <>
                            <td className='align-middle'>
                              <NumericFormat
                                disabled={NotEditable}
                                name={`line-${index}-vatRate`}
                                value={line.vatRate}
                                thousandSeparator=','
                                decimalSeparator='.'
                                decimalScale={2}
                                fixedDecimalScale={false}
                                allowNegative={false}
                                isAllowed={v => v.floatValue === undefined || v.floatValue <= 100}
                                className='form-control form-control-sm border-theme text-center'
                                onValueChange={v => handleLineChange(index, 'vatRate', v.floatValue ?? 0)}
                                style={{ width: '100%', minWidth: '60px' }}
                              />
                            </td>
                            <td className='align-middle fw-bold text-warning text-center'>
                              <div style={{ width: '100%', minWidth: '80px' }}>{formatNumber(line.vatAmount || 0)}</div>
                            </td>
                          </>
                        )}

                        <td className='align-middle'>
                          <div style={{ width: '100%', minWidth: '90px' }}>
                            <strong className='text-theme fs-6'>{formatNumber(line.total || 0)}</strong>
                          </div>
                        </td>

                        {/* الحساب - طويل جداً */}
                        {activeFinance && (
                          <td className='align-middle'>
                            {line.codeCombination ? (
                              <PopoverCustom
                                childern={
                                  <Form.Control
                                    disabled={NotEditable}
                                    readOnly
                                    className='form-control form-control-sm border-theme text-center cursor-pointer'
                                    type='text'
                                    value={line.codeCombination || ''}
                                    onClick={() => handleAccountClick(index)}
                                    style={{
                                      cursor: 'pointer',
                                      minWidth: '240px'
                                    }}
                                  />
                                }
                                Header={t('AccountDetails')}
                                Body={handleShowAccountDetails(index)}
                              />
                            ) : (
                              <button
                                type='button'
                                className='btn btn-outline-theme btn-sm w-100'
                                onClick={() => handleAccountClick(index)}
                                disabled={values.postedFlag || NotEditable}
                                style={{ minWidth: '240px' }}
                              >
                                <i className='fas fa-book me-1'></i> {t('اختر حساب')}
                              </button>
                            )}
                          </td>
                        )}

                        <td className='align-middle text-center'>
                          <button
                            type='button'
                            className='btn btn-sm btn-danger '
                            onClick={() => handleDeleteLine(index)}
                            disabled={values.postedFlag || NotEditable}
                            // title={t('Delete Line')}
                            style={{
                              width: '28px',
                              height: '28px',
                              padding: '0'
                            }}
                          >
                            <i className='fas fa-times'></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Scroll Indicator */}
              {(values.apInvoiceLinesList || []).length > 5 && (
                <div className='mt-2 text-center'>
                  <small className='text-muted'>
                    <i className='fas fa-arrows-alt-v me-1'></i>
                    {t('اسحب لرؤية المزيد من البنود')}
                  </small>
                </div>
              )}
            </div>
          ) : (
            <div className='card-body p-4 text-center'>
              <div className='py-5 text-muted'>
                <i className='fas fa-file-invoice fa-3x mb-3'></i>
                <p className='fs-5'>{t('لا توجد بنود مضافة للفاتورة')}</p>
                <button
                  type='button'
                  className='btn btn-theme mt-2'
                  onClick={handleAddLine}
                  disabled={values.postedFlag || NotEditable}
                >
                  <i className='fas fa-plus me-2'></i>
                  {t('إضافة أول بند')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Section */}
        {(values.apInvoiceLinesList || []).length > 0 && (
          <div className='card-footer d-flex justify-content-between align-items-center'>
            <div className='text-muted small'>
              <i className='fas fa-list me-1'></i>
              عرض {(values.apInvoiceLinesList || []).length} من {(values.apInvoiceLinesList || []).length} بند
            </div>
            <div className='text-theme fw-bold'>
              <i className='fas fa-calculator me-2'></i>
              إجمالي الفاتورة: {totals.total}
            </div>
          </div>
        )}
      </Card>

      {/* Account Selection Modal */}
      <NeatixModal
        show={shawModalAcaount}
        setShow={setShawModalAcaount}
        size='md'
        title={t('Account')}
        headerClassName='bg-theme bg-opacity-25 rounded-top-3 p-3'
        bodyClassName='p-0'
        showHeader={false}
      >
        <CodeCombination
          distributedFlag={values.distributedFlag}
          onAccountSelect={handleAccountSelect}
          accountDetails={accountDetails}
          organizationId={values?.organizationId}
        />
      </NeatixModal>
    </Loading>
  );
};

export default ApInvoiceLines;
